import React, { useState } from 'react';
import { Card, Input, Button, Row, Col, Label, CardBody } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import '../global/GlobalCSS.css';
import { errorToast, successToast } from 'Components/Toasts';
import apiGlobal from 'global/api.global';
import { queryKeyes } from 'shared/queryKeys';
import { encrypt } from 'GenericForms/Helper';
import { useNavigate } from 'react-router-dom';
import env from 'environment_system/env_system';
import ErrorTooltip from 'Components/ErrorTooltip';

interface ForsePasswordChangeType {
  message: string;
}

const ForcedPasswordChange = ({ message }: ForsePasswordChangeType) => {
  const [existingPasswordType, setExistingPasswordType] = useState<'password' | 'text'>('password');
  const [newPasswordType, setNewPasswordType] = useState<'password' | 'text'>('password');
  const [confirmPasswordType, setConfirmPasswordType] = useState<'password' | 'text'>('password');

  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required('Old password is required'),
      newPassword: Yup.string()
        .required('New password is required')
        .min(6, "Password must be at least 6 characters long"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], 'Passwords must match')
        .required('Confirm password is required'),
    }),
    onSubmit: (values: any, action: any) => {
      action.setSubmitting(true);
      const encryptedOldPassword = encrypt(values.oldPassword);
      const encryptedNewPassword = encrypt(values.newPassword);
      const encryptedConfirmPassword = encrypt(values.confirmPassword);

      apiGlobal
        .post(queryKeyes.user.changePassword.url(), {
          old_password: encryptedOldPassword,
          new_password: encryptedNewPassword,
          confirm_new_password: encryptedConfirmPassword,
        })
        .then((res) => {
          if (res.status === 200) {
            successToast('Password changed successfully');
            localStorage.removeItem('authData');
            sessionStorage.removeItem('authData');
            navigate('/login');
          }
        })
        .catch((err) => {
          errorToast(err?.response?.data?.message);
        }).finally(() => {
          action.setSubmitting(false);
        });
    },
  });

  const handleClearFields = () => {
    formik.resetForm();
  };

  return (
    <React.Fragment>
      <Card className="shadow p-4" style={{ maxWidth: '800px', width: '100%', borderRadius: '10px' }}>
        <Row>
          <CardBody>
            <Card className="shadow p-4 mx-auto max-width-20 justify-content-center align-items-center">
              <Row className="justify-content-center">
                <Col className="max-width-16 p-0">
                  <h4 className="mb-3">Change Password</h4>
                  <p>{message}</p>

                  {/* Old Password */}
                  <Label className="mb-0">Existing Password</Label>
                  <div className="position-relative max-width-16 mb-3">
                    <Input
                      onFocus={(e) => e.target.select()}
                      type={existingPasswordType}
                      name="oldPassword"
                      id='oldPassword'
                      placeholder="Enter your password"
                      value={formik.values.oldPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <i
                      className={`mdi ${existingPasswordType === 'password' ? 'mdi-eye' : 'mdi-eye-off'} eye`}
                      onClick={() =>
                        setExistingPasswordType(existingPasswordType === 'password' ? 'text' : 'password')
                      }
                    />
                    {formik?.errors && formik?.touched &&
                      formik?.touched?.oldPassword && formik?.errors?.oldPassword && (
                        <ErrorTooltip
                          target={'oldPassword'}
                          message={formik?.errors?.oldPassword}
                          open={formik?.touched?.oldPassword && formik?.errors?.oldPassword}
                          placement="bottom"
                        />
                      )}
                  </div>

                  {/* New Password */}
                  <Label className="mb-0">New Password</Label>
                  <div className="position-relative max-width-16 mb-3">
                    <Input
                      onFocus={(e) => e.target.select()}
                      type={newPasswordType}
                      name="newPassword"
                      id='newPassword'
                      placeholder="Enter new password"
                      value={formik?.values?.newPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <i
                      className={`mdi ${newPasswordType === 'password' ? 'mdi-eye' : 'mdi-eye-off'} eye`}
                      onClick={() => setNewPasswordType(newPasswordType === 'password' ? 'text' : 'password')}
                    />
                    {formik?.errors && formik?.touched &&
                      formik?.touched?.newPassword && formik?.errors?.newPassword && (
                        <ErrorTooltip
                          target={'newPassword'}
                          message={formik?.errors?.newPassword}
                          open={formik?.touched?.newPassword && formik?.errors?.newPassword}
                          placement="bottom"
                        />
                      )}
                  </div>

                  {/* Confirm Password */}
                  <Label className="mb-0">Confirm New Password</Label>
                  <div className="position-relative max-width-16 mb-3">
                    <Input
                      onFocus={(e) => e.target.select()}
                      type={confirmPasswordType}
                      name="confirmPassword"
                      id='confirmPassword'
                      placeholder="Confirm new password"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <i
                      className={`eye mdi ${confirmPasswordType === 'password' ? 'mdi-eye' : 'mdi-eye-off'
                        }`}
                      onClick={() =>
                        setConfirmPasswordType(confirmPasswordType === 'password' ? 'text' : 'password')
                      }
                    />
                    {formik?.errors && formik?.touched &&
                      formik?.touched?.confirmPassword && formik?.errors?.confirmPassword && (
                        <ErrorTooltip
                          target={'confirmPassword'}
                          message={formik?.errors?.confirmPassword}
                          open={formik?.touched?.confirmPassword && formik?.errors?.confirmPassword}
                          placement="bottom"
                        />
                      )}
                  </div>

                  <div className="d-flex justify-content-between gap-4">
                    <Button onClick={handleClearFields} className="btn btn-danger" color="danger">
                      Clear
                    </Button>
                    <Button
                      onClick={(e: any) => formik.handleSubmit(e)}
                      className="btn btn-primary"
                      color="primary"
                      disabled={formik.isSubmitting || env.offlineCopy}
                    >
                      Reset Password
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card>
          </CardBody>
        </Row>
      </Card>
    </React.Fragment>
  );
};

export default ForcedPasswordChange;
